import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { createTabChannel, getUserDetails, hasStudyViewingPermission, makePostCall } from '../../utils/helper';
import { Layout, List, Card, Typography, Spin, message, Button } from 'antd';
import { MenuFoldOutlined, MenuUnfoldOutlined, UnorderedListOutlined } from '@ant-design/icons';
import moment from 'moment';
import ReportEditor from '.';
import { launchViewerOnDualMonitor, checkMonitorSetup } from '../../utils/MonitorManager';
import './reporting-page.css';

const { Content, Sider } = Layout;
const { Text } = Typography;

const ReportingPage = () => {
  const [searchParams] = useSearchParams();
  const [ordersList, setOrdersList] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const order_ids = searchParams.get('order_ids');
  const study_id = searchParams.get('study_id');

  const channel = createTabChannel();

  useEffect(() => {
    return () => channel.cleanup();
  }, []);

  useEffect(() => {
    if (order_ids?.length > 0) {
      fetchOrderDetails(order_ids, null);
    } else if (study_id) {
      fetchOrderDetails('',study_id)
    }
  }, [order_ids, study_id]);

  const fetchOrderDetails = async (order_ids, study_id=null) => {
    setLoading(true);
    try {
      const resp = await makePostCall('/orders-list-details', {
        order_ids: order_ids === '' ? [] : order_ids.split(','),
        study_uid: study_id,
        user_id: getUserDetails()?.username
      });

      const ordData = resp?.data;
      const conflictingStudies = ordData.conflictingStudies || [];
      const reportingDisabledStudies = ordData.reportingDisabledStudies || [];
      const signedOffStudies = ordData.signedOffStudies || [];

      [...conflictingStudies, ...reportingDisabledStudies, ...signedOffStudies].forEach((itm) => {
        const reason = itm.opened_by
          ? `last opened by '${itm.opened_by}'`
          : itm.reason || 'reporting disabled';
        message.warning(`${itm.study} - ${reason}`, 10);
      });

      setOrdersList(ordData?.data || []);

      if ((ordData?.data || []).length === 0) {
        message.info("No studies available for reporting");
        return;
      }

      // Select the first order by default if available
      if (ordData?.data?.length > 0) {
        handleOrderSelect(ordData.data[0], true, -1);
        if (!study_id && order_ids !== '' && hasStudyViewingPermission(getUserDetails())) {
          launchViewerForStudy(ordData.data[0]);
        }
      }
    } catch (err) {
      console.error('Failed to fetch order details:', err);
      message.error('Failed to fetch order details');
    } finally {
      setLoading(false);
    }
  };

  const launchViewerForStudy = async (order: any): Promise<Window | null> => {
    return await launchViewerOnDualMonitor(order?.ps_study_uid, order);
  };

  const handleOrderSelect = async (order, isInitial, index) => {
    const response = await makePostCall('/study-report-details', { user_id: getUserDetails()?.username, order_id: order?.pacs_order?.pacs_ord_id });
    if (response?.data?.success) {
      setSelectedOrder(order);
      setSelectedIndex(index);
    } else {
      message.error(response?.data?.message);
      setSelectedOrder(null);
      setSelectedIndex(-1);
      return;
    }

    if (!isInitial && hasStudyViewingPermission(getUserDetails())) {
      channel.sendUrlUpdate(`/viewer/viewer?StudyInstanceUIDs=${order?.ps_study_uid}`);
    } else {
      setSelectedIndex(0)
    }
  };

  const handlePostSaving = (status, patDetails) => {
    try {
      if (status === 'signoff') {

        if (selectedIndex === ordersList?.length - 1) {
          message.info("Closing the window..");
          setTimeout(() => {
            window.open("about:blank", "_self");
            window.close();
          }, 3000)
        }
        else {
          message.info("Changing the study");
          setTimeout(() => {
            setSelectedOrder(ordersList[selectedIndex + 1]);
            setSelectedIndex(selectedIndex + 1);
          }, 1000);
        }
      }
    } catch (e) {
      console.error('Failed to save study report:', e);
      message.error('Failed to save study report');
    }
  }

  return (
    <Layout className="reporting-page-layout">
      <Layout>
        <Sider
          width="16.25rem"
          collapsedWidth="3.125rem"
          collapsed={collapsed}
          onCollapse={setCollapsed}
          theme="light"
          className="reporting-page-sider"
        >
          <div className="sider-header">
            <h4 className="sider-title">
              {!collapsed ? (
                <>
                  <UnorderedListOutlined className="sider-title-icon" />
                  <span>Selected Studies</span>
                </>
              ) : ''}
            </h4>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
            />
          </div>
          <div className="sider-content">
            <Spin spinning={loading}>
              {!collapsed && (
                <List
                  dataSource={ordersList}
                  renderItem={(order, idx) => (
                    <List.Item
                      onClick={() => handleOrderSelect(order, false, idx)}
                      className={`study-list-item ${selectedIndex === idx ? 'study-list-item-selected' : 'study-list-item-default'}`}
                    >
                      <Card
                        className={`study-card ${selectedIndex === idx ? 'study-card-selected' : 'study-card-default'}`}
                        bordered={false}
                        hoverable
                      >
                        <div className="patient-name-text">
                          {order.pacs_order?.patient?.pat_name}
                        </div>
                        <div className="study-details-text" title={`${order?.pacs_order?.po_modality} | ${order?.pacs_order?.po_diag_desc}`}>
                          {order?.pacs_order?.po_modality} | {order?.pacs_order?.po_diag_desc}
                        </div>
                        <div className="study-date-text">
                          {moment(order?.pacs_order?.po_req_time).format("DD/MM/YYYY hh:mmA")}
                        </div>
                      </Card>
                    </List.Item>
                  )}
                />
              )}
            </Spin>
          </div>
        </Sider>
        <Layout className={`content-layout`}>
          <Content>
            <div className="content-wrapper">
              {selectedOrder?.pacs_order ? (
                <ReportEditor
                  handlePostSaving={handlePostSaving}
                  patientDetails={selectedOrder}
                  cancel={() => {
                    window.open("about:blank", "_self");
                    window.close();
                  }}
                />
                // <div>{selectedOrder?.po_diag_desc}</div>
              ) : (
                <div className="empty-state">
                  <Text type="secondary">Select an order to view/edit the report</Text>
                </div>
              )}
            </div>
          </Content>
        </Layout>
      </Layout>
    </Layout >
  );
};

export default ReportingPage;
